/**
 * @letsdoit/quick-add — deterministic Todoist Quick Add parsing (English and Chinese).
 *
 * `parseQuickAdd(text, ctx)` recognizes `#Project`, `/Section`, `@label` (or `%label`), `p1`–`p4`
 * (`!!1`–`!!4`), `+assignee`, `{deadline}`, `!reminder`, `for 30min` / `持续30分钟`, `// description`
 * and a natural-language due date anywhere in the text (via `@letsdoit/due-parser`), and returns
 * the content with those tokens removed plus every token with its `[start, end)` range.
 * Pure and dependency-free apart from due-parser: runs in Workers, Node, browsers and React Native.
 */
import { type Due, detectLang, type ParseContext, parseDue } from "@letsdoit/due-parser";
import type {
  CollaboratorRef,
  NamedRef,
  QuickAddContext,
  QuickAddResult,
  QuickReminder,
  SectionRef,
  Token,
} from "./types";

export type {
  CollaboratorRef,
  NamedRef,
  QuickAddContext,
  QuickAddResult,
  QuickReminder,
  Range,
  SectionRef,
  Token,
  TokenType,
} from "./types";

const isWs = (c: string | undefined) => c !== undefined && /\s/.test(c);
const isCjk = (c: string | undefined) => c !== undefined && /[㐀-鿿]/.test(c);
const isAlnum = (c: string | undefined) => c !== undefined && /[A-Za-z0-9]/.test(c);
/** Punctuation that may directly follow a token (and is never part of a label name's tail). */
const isPunct = (c: string | undefined) => c !== undefined && /[,.;:!?，。；：！？、)）\]】"'”’]/.test(c);
const lower = (s: string) => s.toLowerCase();

/** Marker characters; a name may end right before one (`买菜#家庭@电话`). */
const MARKERS = new Set(["#", "@", "%", "+", "{", "!"]);

/** Characters a backslash escapes anywhere; letters/digits/CJK only at the start of a word. */
const ESCAPABLE = new Set(["#", "@", "%", "/", "+", "!", "{", "}", "\\", " ", '"']);

/** Characters Chinese date expressions start or end with (the vocabulary of due-parser's zh grammar). */
const ZH_DATE_CHARS = new Set(
  "〇零一二两三四五六七八九十百千万上下中今从傍元内凌初午半后前周圣夜大天季工年早明星昨晚晨月本每深清第至节这隔起到直截日号號点點分钟刻时時末底旬头始期拜礼禮天间开度个旦诞情".split(
    "",
  ),
);

/** Single English words that due-parser accepts but that are far more often ordinary words in a task. */
const EN_STOPWORDS = new Set(["tom", "tod", "sun", "sat", "mon", "wed", "morning", "evening", "weekend", "noon"]);

const MAX_DATE_CHARS = 48;
const MAX_DATE_WORDS = 8;

class Text {
  readonly s: string;
  readonly len: number;
  /** Char is escaped by a preceding backslash (never a marker, never part of a date). */
  readonly literal: boolean[];
  /** Char is dropped from the content (an escaping backslash). */
  readonly hidden: boolean[];
  /** Char belongs to a recognized token. */
  readonly used: boolean[];
  /** Char is inside double quotes (protected from date recognition). */
  readonly quoted: boolean[];

  constructor(s: string) {
    this.s = s;
    this.len = s.length;
    this.literal = new Array(s.length).fill(false);
    this.hidden = new Array(s.length).fill(false);
    this.used = new Array(s.length).fill(false);
    this.quoted = new Array(s.length).fill(false);
    for (let i = 0; i < s.length - 1; i++) {
      if (s[i] !== "\\") continue;
      const next = s[i + 1] as string;
      const wordStart = i === 0 || isWs(s[i - 1]);
      if (ESCAPABLE.has(next) || (wordStart && (isAlnum(next) || isCjk(next)))) {
        this.hidden[i] = true;
        this.literal[i + 1] = true;
        i++;
      }
    }
    let open = -1;
    for (let i = 0; i < s.length; i++) {
      const c = s[i];
      if (this.literal[i]) continue;
      if (open < 0 && (c === '"' || c === "“")) open = i;
      else if (open >= 0 && (c === '"' || c === "”")) {
        for (let j = open; j <= i; j++) this.quoted[j] = true;
        open = -1;
      }
    }
  }

  free(i: number): boolean {
    return i < this.len && !this.used[i] && !this.hidden[i];
  }

  /** Free and not escaped/quoted: may be part of a date. */
  dateable(i: number): boolean {
    return this.free(i) && !this.literal[i] && !this.quoted[i];
  }

  use(start: number, end: number): void {
    for (let i = start; i < end; i++) this.used[i] = true;
  }

  /** Content without escapes, with tokens removed and whitespace collapsed. */
  content(end: number): string {
    let out = "";
    for (let i = 0; i < end; i++) {
      if (this.hidden[i]) continue;
      out += this.used[i] ? " " : this.s[i];
    }
    return out.replace(/\s+/g, " ").trim();
  }

  /** The text of a range without escaping backslashes. */
  unescaped(start: number, end: number): string {
    let out = "";
    for (let i = start; i < end; i++) if (!this.hidden[i]) out += this.s[i];
    return out;
  }

  /**
   * Matches `name` case-insensitively at `i` (escaped chars compare as themselves, so `My\ Project`
   * matches "My Project"); returns the end offset, or -1. The match must end at a word boundary.
   */
  matchName(i: number, name: string): number {
    let j = i;
    for (let k = 0; k < name.length; k++) {
      if (j < this.len && this.hidden[j]) j++;
      if (j >= this.len || this.used[j]) return -1;
      if (lower(this.s[j] as string) !== lower(name[k] as string)) return -1;
      j++;
    }
    const next = this.s[j];
    if (j < this.len && !this.used[j] && !isWs(next) && !isPunct(next) && !MARKERS.has(next as string)) return -1;
    return j;
  }

  /** Reads a word from `i` up to unescaped whitespace, without trailing punctuation. */
  readWord(i: number): number {
    let j = i;
    while (
      j < this.len &&
      !this.used[j] &&
      !((isWs(this.s[j]) || MARKERS.has(this.s[j] as string)) && !this.literal[j])
    )
      j++;
    while (j > i && isPunct(this.s[j - 1]) && !this.literal[j - 1]) j--;
    return j;
  }
}

/** Longest match of any of `candidates` (keyed names) at `i`. */
function longestMatch<T>(
  t: Text,
  i: number,
  candidates: { key: string; value: T }[],
): { end: number; value: T } | null {
  let best: { end: number; value: T } | null = null;
  for (const c of candidates) {
    if (!c.key) continue;
    const end = t.matchName(i, c.key);
    if (end > 0 && (!best || end > best.end)) best = { end, value: c.value };
  }
  return best;
}

const CN_DIGITS: Record<string, number> = {
  零: 0,
  〇: 0,
  一: 1,
  二: 2,
  两: 2,
  三: 3,
  四: 4,
  五: 5,
  六: 6,
  七: 7,
  八: 8,
  九: 9,
};

/** Arabic or small Chinese numbers (up to 99, plus 半 = 0.5). */
function num(s: string): number | null {
  if (/^\d+(?:\.\d+)?$/.test(s)) return Number(s);
  if (s === "半") return 0.5;
  const m = /^([一二两三四五六七八九])?(十)?([一二三四五六七八九])?$/.exec(s);
  if (!m || s === "") return null;
  if (!m[2]) return m[1] && !m[3] ? (CN_DIGITS[m[1]] as number) : null;
  return (m[1] ? (CN_DIGITS[m[1]] as number) : 1) * 10 + (m[3] ? (CN_DIGITS[m[3]] as number) : 0);
}

function unitMinutes(unit: string): number {
  const u = lower(unit);
  if (u.startsWith("d") || u === "天") return 1440;
  if (u.startsWith("h") || u === "小时" || u === "个小时" || u === "钟头" || u === "个钟头") return 60;
  return 1;
}

const DEFAULT_DATES = (): ParseContext => ({ now: new Date(), userTimezone: "UTC" });

/** Parses `s` as a date; null unless it is a real (non-"no date") due. */
function tryDate(s: string, dates: ParseContext): Due | null {
  const r = parseDue({ string: s }, dates);
  return r.ok && r.due ? r.due : null;
}

function startOk(t: Text, p: number): boolean {
  if (!t.dateable(p)) return false;
  const c = t.s[p];
  const prev = p > 0 ? t.s[p - 1] : undefined;
  if (isAlnum(c))
    return prev === undefined || isWs(prev) || isCjk(prev) || /[(（[【,，"“'‘]/.test(prev) || !t.free(p - 1);
  if (isCjk(c)) return ZH_DATE_CHARS.has(c as string) && !isAlnum(prev);
  return false;
}

function endOk(t: Text, e: number): boolean {
  const last = t.s[e - 1];
  const next = e < t.len ? t.s[e] : undefined;
  if (isAlnum(last)) return next === undefined || isWs(next) || isCjk(next) || isPunct(next) || !t.free(e);
  if (isCjk(last)) return ZH_DATE_CHARS.has(last as string) && !isAlnum(next);
  return false;
}

/**
 * Finds a date span. With `at`, only spans starting there; otherwise the earliest start wins and,
 * at that start, the longest span that due-parser accepts.
 */
function findDate(
  t: Text,
  dates: ParseContext,
  opts: { at?: number; needTime?: boolean } = {},
): { start: number; end: number; due: Due } | null {
  const starts = opts.at !== undefined ? [opts.at] : [...Array(t.len).keys()];
  for (const p of starts) {
    if (opts.at === undefined ? !startOk(t, p) : !t.dateable(p)) continue;
    let limit = p;
    let words = 1;
    while (limit < t.len && limit - p < MAX_DATE_CHARS && t.dateable(limit)) {
      if (isWs(t.s[limit]) && !isWs(t.s[limit - 1]) && ++words > MAX_DATE_WORDS) break;
      limit++;
    }
    for (let e = limit; e > p; e--) {
      if (!endOk(t, e)) continue;
      const span = t.s.slice(p, e);
      if (!/[A-Za-z0-9㐀-鿿]/.test(span)) continue;
      if (!/\s/.test(span) && !isCjk(span[0]) && EN_STOPWORDS.has(lower(span))) continue;
      const due = tryDate(span, dates);
      if (due && (!opts.needTime || due.date.length > 10)) return { start: p, end: e, due };
    }
  }
  return null;
}

const PRIORITY_RE = /(?:[pP]([1-4])|!!([1-4]))/y;
const REL_REMINDER_EN = /!(\d+)\s*(minutes?|mins?|m|hours?|hrs?|h|days?|d)(b)?(?:\s+before)?/iy;
const REL_REMINDER_ZH = /!(?:提前)?\s*(\d+|[一二两三四五六七八九十半]+)\s*(分钟|分|个小时|小时|个钟头|钟头|天)前?/y;
const DURATION_EN = /for\s+(\d+(?:\.\d+)?)\s*(minutes?|mins?|m|hours?|hrs?|h)(?:\s*(\d+)\s*(minutes?|mins?|m))?/iy;
const DURATION_ZH =
  /(?:持续|用时|时长|耗时)\s*(\d+(?:\.\d+)?|[一二两三四五六七八九十半]+)\s*(个半)?\s*(分钟|个小时|小时|个钟头|钟头)(半)?/y;

function stickyAt(re: RegExp, s: string, i: number): RegExpExecArray | null {
  re.lastIndex = i;
  return re.exec(s);
}

/** A token boundary after a match: end, whitespace, punctuation, CJK or an already used char. */
function boundaryAfter(t: Text, e: number): boolean {
  const c = t.s[e];
  return e >= t.len || isWs(c) || isPunct(c) || isCjk(c) || !t.free(e);
}

/** A marker may start a word: at the start, after whitespace or directly after CJK text. */
function markerStart(t: Text, i: number, allowAfterCjk = true): boolean {
  if (!t.free(i) || t.literal[i]) return false;
  const prev = i > 0 ? t.s[i - 1] : undefined;
  return prev === undefined || isWs(prev) || (allowAfterCjk && isCjk(prev)) || t.used[i - 1] === true;
}

/** Parses Todoist Quick Add text into content and structured, ranged tokens. */
export function parseQuickAdd(text: string, ctx: QuickAddContext = {}): QuickAddResult {
  const t = new Text(text);
  const s = text;
  const tokens: Token[] = [];
  const dates = ctx.dates ?? DEFAULT_DATES();
  const add = (tok: Token) => {
    tokens.push(tok);
    t.use(tok.start, tok.end);
  };

  // 1. Description: everything after the first ` //` (must be preceded by whitespace or start the text).
  let bodyEnd = t.len;
  let description: string | null = null;
  for (let k = 0; k < t.len - 1; k++) {
    if (s[k] === "/" && s[k + 1] === "/" && t.free(k) && !t.literal[k] && (k === 0 || isWs(s[k - 1]))) {
      description = s.slice(k + 2).trim();
      add({ type: "description", start: k, end: t.len, text: s.slice(k), description });
      bodyEnd = k;
      break;
    }
  }

  // 2. Project: the first `#` that matches an existing project (longest name wins).
  let project: NamedRef | null = null;
  const projectKeys = (ctx.projects ?? []).map((p) => ({ key: p.name, value: p }));
  for (let i = 0; i < bodyEnd && !project; i++) {
    if (s[i] !== "#" || !markerStart(t, i)) continue;
    const m = longestMatch(t, i + 1, projectKeys);
    if (m) {
      project = m.value;
      add({ type: "project", start: i, end: m.end, text: s.slice(i, m.end), project: m.value });
    }
  }
  const targetProjectId = project?.id ?? ctx.defaultProjectId ?? null;
  const sectionKeys = (ctx.sections ?? [])
    .filter((x) => x.projectId === targetProjectId)
    .map((x) => ({ key: x.name, value: x }));
  const labelKeys = (ctx.labels ?? []).map((name) => ({ key: name, value: name }));
  const collaboratorKeys = (ctx.collaborators ?? []).flatMap((c) => {
    const keys = [{ key: c.name, value: c }];
    const first = c.name.trim().split(/\s+/)[0];
    if (first && first !== c.name) keys.push({ key: first, value: c });
    if (c.email) {
      keys.push({ key: c.email, value: c });
      keys.push({ key: c.email.split("@")[0] as string, value: c });
    }
    return keys;
  });

  // 3. The other markers, left to right.
  let section: SectionRef | null = null;
  let priority: number | null = null;
  let assignee: CollaboratorRef | null = null;
  let deadline: { string: string; date: string } | null = null;
  const labels: { name: string; isNew: boolean }[] = [];
  const reminders: QuickReminder[] = [];
  for (let i = 0; i < bodyEnd; i++) {
    const c = s[i];
    if (!t.free(i)) continue;
    if (c === "/" && !section && markerStart(t, i)) {
      const m = longestMatch(t, i + 1, sectionKeys);
      if (m) {
        section = m.value;
        add({ type: "section", start: i, end: m.end, text: s.slice(i, m.end), section: m.value });
      }
    } else if ((c === "@" || c === "%") && markerStart(t, i) && t.free(i + 1) && !isWs(s[i + 1])) {
      const m = longestMatch(t, i + 1, labelKeys);
      const end = m ? m.end : t.readWord(i + 1);
      const name = m ? m.value : t.unescaped(i + 1, end).trim();
      if (!name) continue;
      if (!labels.some((l) => lower(l.name) === lower(name))) labels.push({ name, isNew: !m });
      add({ type: "label", start: i, end, text: s.slice(i, end), name, isNew: !m });
    } else if (c === "+" && !assignee && markerStart(t, i)) {
      const m = longestMatch(t, i + 1, collaboratorKeys);
      if (m) {
        assignee = m.value;
        add({ type: "assignee", start: i, end: m.end, text: s.slice(i, m.end), assignee: m.value });
      }
    } else if (c === "{" && !deadline && markerStart(t, i)) {
      let j = i + 1;
      while (j < bodyEnd && !(s[j] === "}" && !t.literal[j])) j++;
      if (j >= bodyEnd) continue;
      const inner = s.slice(i + 1, j).trim();
      const due = inner ? tryDate(inner, dates) : null;
      if (due) {
        deadline = { string: inner, date: due.date.slice(0, 10) };
        add({ type: "deadline", start: i, end: j + 1, text: s.slice(i, j + 1), ...deadline });
      }
    } else if ((c === "p" || c === "P" || c === "!") && priority === null && markerStart(t, i, false)) {
      const m = stickyAt(PRIORITY_RE, s, i);
      if (m && boundaryAfter(t, i + m[0].length) && !isCjk(s[i + m[0].length])) {
        priority = 5 - Number(m[1] ?? m[2]);
        const end = i + m[0].length;
        add({ type: "priority", start: i, end, text: s.slice(i, end), priority });
        continue;
      }
      if (c === "!" && s[i + 1] !== "!" && markerStart(t, i)) {
        const r = reminderAt(t, i, dates);
        if (r) {
          reminders.push(r.reminder);
          add({ type: "reminder", start: i, end: r.end, text: s.slice(i, r.end), reminder: r.reminder });
        }
      }
    } else if (c === "!" && s[i + 1] !== "!" && markerStart(t, i)) {
      const r = reminderAt(t, i, dates);
      if (r) {
        reminders.push(r.reminder);
        add({ type: "reminder", start: i, end: r.end, text: s.slice(i, r.end), reminder: r.reminder });
      }
    }
  }

  // 4. Duration ("for 30min", "持续30分钟").
  let duration: { amount: number; unit: "minute" } | null = null;
  for (let i = 0; i < bodyEnd && !duration; i++) {
    if (!t.dateable(i)) continue;
    const prev = i > 0 ? s[i - 1] : undefined;
    let m: RegExpExecArray | null = null;
    let minutes = 0;
    if ((s[i] === "f" || s[i] === "F") && (prev === undefined || isWs(prev) || isCjk(prev))) {
      m = stickyAt(DURATION_EN, s, i);
      if (m && !boundaryAfter(t, i + m[0].length)) m = null;
      if (m) minutes = Number(m[1]) * unitMinutes(m[2] as string) + (m[3] ? Number(m[3]) : 0);
    } else if (isCjk(s[i])) {
      m = stickyAt(DURATION_ZH, s, i);
      const n = m ? num(m[1] as string) : null;
      if (m && n !== null) minutes = n * unitMinutes(m[3] as string) + (m[2] || m[4] ? 30 : 0);
      else m = null;
    }
    if (!m) continue;
    const end = i + m[0].length;
    let clean = true;
    for (let j = i; j < end; j++) if (!t.dateable(j)) clean = false;
    const amount = Math.round(minutes);
    if (!clean || amount <= 0) continue;
    duration = { amount, unit: "minute" };
    add({ type: "duration", start: i, end, text: s.slice(i, end), ...duration });
  }

  // 5. Due date: the earliest (then longest) span due-parser understands.
  let due: QuickAddResult["due"] = null;
  if (ctx.parseDates !== false) {
    const found = findDate(t, dates);
    if (found) {
      const str = s.slice(found.start, found.end);
      due = { string: str, lang: found.due.lang || detectLang(str), due: found.due };
      add({ type: "due", start: found.start, end: found.end, text: str, ...due });
    }
  }

  tokens.sort((a, b) => a.start - b.start);
  return {
    content: t.content(bodyEnd),
    description,
    project,
    section,
    labels,
    priority,
    assignee,
    due,
    deadline,
    duration,
    reminders,
    tokens,
  };
}

/** `!…` reminders: relative offsets, or an absolute natural-language moment with a time. */
function reminderAt(t: Text, i: number, dates: ParseContext): { end: number; reminder: QuickReminder } | null {
  for (const re of [REL_REMINDER_EN, REL_REMINDER_ZH]) {
    const m = stickyAt(re, t.s, i);
    if (!m) continue;
    const end = i + m[0].length;
    const n = num(m[1] as string);
    if (n === null || !boundaryAfter(t, end)) continue;
    return { end, reminder: { type: "relative", minuteOffset: Math.round(n * unitMinutes(m[2] as string)) } };
  }
  if (!t.free(i + 1) || isWs(t.s[i + 1])) return null;
  const found = findDate(t, dates, { at: i + 1, needTime: true });
  if (!found) return null;
  return { end: found.end, reminder: { type: "absolute", string: t.s.slice(found.start, found.end), due: found.due } };
}
